import type { ReactNode } from "react";
import ToggleSwitch from "../../../shared/ToggleSwitch";
import { ADMIN_PERMISSIONS, buildAssignablePermissionState, type AdminPermissionKey, type AdminPermissionState } from "../../../core/adminPermissions";

export type AdminFormState = {
  name: string;
  email: string;
  password: string;
  role: "manager" | "support";
  isActive: boolean;
  permissions: AdminPermissionState[];
};

export const createEmptyAdminForm = (): AdminFormState => ({
  name: "",
  email: "",
  password: "",
  role: "manager",
  isActive: true,
  permissions: buildAssignablePermissionState(),
});

const FormField = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="form-field">
    <label className="modal-label">{label}</label>
    {children}
  </div>
);

export const AdminAccountFields = ({
  form,
  onChange,
  passwordOptional = false,
  disableActive = false,
}: {
  form: AdminFormState;
  onChange: (updates: Partial<AdminFormState>) => void;
  passwordOptional?: boolean;
  disableActive?: boolean;
}) => (
  <div className="admin-form-grid">
    <FormField label="Full Name">
      <input
        className="admin-input"
        value={form.name}
        onChange={(event) => onChange({ name: event.target.value })}
        required
      />
    </FormField>
    <FormField label="Email Address">
      <input
        type="email"
        className="admin-input"
        value={form.email}
        onChange={(event) => onChange({ email: event.target.value })}
        required
      />
    </FormField>
    <FormField
      label={passwordOptional ? "New Password (optional)" : "Password"}
    >
      <input
        type="password"
        className="admin-input"
        value={form.password}
        onChange={(event) => onChange({ password: event.target.value })}
        required={!passwordOptional}
        minLength={6}
        placeholder={
          passwordOptional
            ? "Leave blank to keep current password"
            : "Minimum 6 characters"
        }
      />
    </FormField>
    <FormField label="Role">
      <RoleSelect value={form.role} onChange={(role) => onChange({ role })} />
    </FormField>
    <div className="form-field admin-status-field">
      <ToggleSwitch
        variant="row"
        label="Status"
        hint="Admin can sign in to the panel when active"
        checked={form.isActive}
        disabled={disableActive}
        onChange={(isActive) => onChange({ isActive })}
      />
    </div>
  </div>
);

export const RoleSelect = ({
  value,
  onChange,
  disabled = false,
}: {
  value: AdminFormState["role"];
  onChange: (role: AdminFormState["role"]) => void;
  disabled?: boolean;
}) => (
  <select
    className="admin-input"
    value={value}
    disabled={disabled}
    onChange={(event) => onChange(event.target.value as AdminFormState["role"])}
  >
    {(["manager", "support"] as const).map((role) => (
      <option key={role} value={role}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </option>
    ))}
  </select>
);

export const PermissionPicker = ({
  permissions,
  onChange,
}: {
  permissions: AdminPermissionState[];
  onChange: (permissions: AdminPermissionState[]) => void;
}) => {
  const toggle = (key: AdminPermissionKey, isActive: boolean) =>
    onChange(permissions.map((permission) => permission.name === key ? { ...permission, isActive } : permission));

  return (
    <section className="table-card permission-panel">
      <div className="table-head permission-panel-head">
        <div><h2>Module Permissions</h2><p>Select which admin modules this account can access.</p></div>
        <button type="button" className="btn-sm btn-outline" onClick={() => onChange(buildAssignablePermissionState(ADMIN_PERMISSIONS.filter((p) => p.key !== "profile").map((p) => p.key)))}>Enable All</button>
      </div>
      <div className="permission-grid admin-card-body">
        {ADMIN_PERMISSIONS.filter(({ key }) => key !== "profile").map(({ key, label, description }) => {
          const active = permissions.some((permission) => permission.name === key && permission.isActive);
          return <label className={`permission-item ${active ? "is-enabled" : ""}`} key={key}>
            <input type="checkbox" checked={active} onChange={(event) => toggle(key, event.target.checked)} />
            <span><strong>{label}</strong><small>{description}</small></span>
          </label>;
        })}
      </div>
    </section>
  );
};
